import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaUsers, FaShieldAlt, FaEdit, FaTrash } from 'react-icons/fa';
import { getRoles, deleteRole } from '../redux/Slices/authSlice';
import { useTitle } from '../context/TitleContext';
import { toast } from "react-toastify";
import PermissionMotionButton from '../components/PermissionMotionButton';
import PermissionButton from '../components/PermissionButton';
import { translatePermission } from '../Utils/permissionLabels';
import Pagination from '../components/Pagination';

const Roles = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { roles, meta, isLoading: loading, error, success } = useSelector((state) => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);

  useEffect(() => {
    setTitle('الصلاحيات');
    dispatch(getRoles(currentPage));
  }, [dispatch, setTitle, currentPage]);

  useEffect(() => {
    if (success) {
      toast.success(success, { position: "top-center", zIndex: 9999 });
      dispatch(getRoles(currentPage));
    }
    if (error) {
      toast.error(error, { position: "top-center", zIndex: 9999 });
    }
  }, [success, error, dispatch, currentPage]);

  const handleDelete = (role) => {
    setDeletingRole(role);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingRole) {
      dispatch(deleteRole(deletingRole.id));
      setShowDeleteModal(false);
      setDeletingRole(null);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };



  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (loading && (!roles || roles.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .role-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .role-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .role-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            🛡 الصلاحيات
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>إدارة وتعديل الصلاحيات المخصصة لأفراد فريق العمل والأدمن</p>
        </div>
        <PermissionMotionButton
          permission="create-roles"
          onClick={() => navigate("/add-role")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          <FaPlus size={12} /> إضافة صلاحية جديدة
        </PermissionMotionButton>
      </div>

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!roles || roles.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <FaShieldAlt size={48} className="text-muted mb-3" />
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد صلاحيات مسجلة</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="role-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الاسم</th>
                    <th>عدد الصلاحيات الفرعية</th>
                    <th>عدد المستخدمين</th>
                    <th>الصلاحيات</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role, index) => (
                    <tr key={role.id}>
                      <td>{index + 1}</td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>
                        <div>
                          <strong>{role.name}</strong>
                          <br />
                          <small className="text-muted">ID: {role.id}</small>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#0284c7", background: "#e0f2fe", border: "1px solid #bae6fd", padding: "3px 10px", borderRadius: 20 }}>
                          {role.permissions_count} صلاحية
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", background: "#d1fae5", border: "1px solid #a7f3d0", padding: "3px 10px", borderRadius: 20 }}>
                          <FaUsers style={{ marginLeft: 4 }} />
                          {role.users_count ?? 0} مستخدم
                        </span>
                      </td>
                      <td style={{ maxWidth: '300px' }}>
                        <div className="d-flex flex-wrap gap-1 justify-content-center">
                          {role.permissions?.slice(0, 3).map((permission, idx) => (
                            <span key={idx} style={{ fontSize: 11, fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "3px 10px", borderRadius: 20, border: "1px solid #e2e8f0" }} title={permission.name}>
                              {translatePermission(permission.name)}
                            </span>
                          ))}
                          {role.permissions?.length > 3 && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f8fafc", padding: "3px 10px", borderRadius: 20, border: "1px solid #e2e8f0" }}>
                              +{role.permissions.length - 3} أخرى
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => {
                              sessionStorage.setItem(`role_${role.id}`, JSON.stringify({
                                id: role.id,
                                name: role.name,
                                permissions: role.permissions || []
                              }));
                              navigate(`/edit-role/${role.id}`);
                            }}
                            permission="edit-roles"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(role)}
                            permission="delete-roles"
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingRole(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذه الصلاحية؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                🛡 {deletingRole?.name}
              </div>
              {deletingRole?.permissions_count > 0 && (
                <p style={{ margin: "10px 0 0", color: "var(--warning-color)", fontSize: 13 }}>
                  ⚠️ <strong>تحذير:</strong> هذه الصلاحية تحتوي على {deletingRole.permissions_count} صلاحية فرعية مرتبطة.
                </p>
              )}
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingRole(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer" }}>حذف الصلاحية</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
